import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import update from 'immutability-helper'
import * as SettingsActions from '../../../state/actions/message-settings'
import * as ATActions from '../../../state/actions/appointment-types'
import PlusOneButton from '../../assets/PlusOneButton'
import SettingsNumberLine from '../../assets/SettingsNumberLine'
import SettingsNumberLineNegativeScale from '../../assets/SettingsNumberLineNegativeScale'
import SettingsNumberLineWeeksScale from '../../assets/SettingsNumberLineWeeksScale'
import { selectedApptTypesById } from '../utils'
import { AddDialog, UndoDeleteDialog } from '../Dialogs'
import Handle from '../Handle'
import MessageSettingApptTypePicker from '../MessageSettingApptTypePicker'
import customApptTypeSliderActions from './customApptTypeSliderActions'
import SliderSettingPill from './SliderSettingPill'

const createSlider = (displayName, styles, altStyles = false) => {
  class SpecializedSlider extends Component {
    static defaultProps = {
      selectedApptTypes: [],
      sliderName: '',
    }

    static propTypes = {
      activateSetting: PropTypes.func,
      availableAppointmentTypesById: PropTypes.objectOf(
        PropTypes.shape({
          id: PropTypes.string,
          appointment_type_guid: PropTypes.string,
          practice_id: PropTypes.number,
          code: PropTypes.string,
          description: PropTypes.string,
          visibility: PropTypes.string,
          updated_at: PropTypes.string,
          created_at: PropTypes.string,
          online_scheduling: PropTypes.bool,
        }),
      ),
      availableAppointmentTypeIds: PropTypes.arrayOf(PropTypes.string),
      appointmentTypesById: PropTypes.objectOf(
        PropTypes.shape({
          id: PropTypes.string,
        }),
      ),
      byId: PropTypes.objectOf(
        PropTypes.shape({
          active: PropTypes.bool,
          always_send: PropTypes.bool,
          body: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
          created_at: PropTypes.string,
          custom_message_setting_id: PropTypes.number,
          id: PropTypes.number.isRequired,
          message_setting_type: PropTypes.string.isRequired,
          message_type: PropTypes.string.isRequired,
          offset: PropTypes.number.isRequired,
          practice_id: PropTypes.number.isRequired,
          subject: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
          updated_at: PropTypes.string,
        }),
      ),
      children: PropTypes.oneOfType([
        PropTypes.element,
        PropTypes.arrayOf(PropTypes.element),
      ]),
      destroyCustomMessageSetting: PropTypes.func,
      id: PropTypes.number,
      isNegative: PropTypes.bool,
      isPopoverOpen: PropTypes.bool,
      isPostAppt: PropTypes.bool,
      isWeeks: PropTypes.bool,
      lastRemovedId: PropTypes.number,
      locationId: PropTypes.string,
      messageSettingType: PropTypes.string,
      name: PropTypes.string,
      nextTmpId: PropTypes.number,
      selectedApptTypes: PropTypes.arrayOf(PropTypes.string),
      settingIds: PropTypes.arrayOf(PropTypes.number),
      sliderName: PropTypes.string,
      sliderSettings: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
        }),
      ),
      undoDeactivate: PropTypes.func,
      updateCustomMessageSetting: PropTypes.func,
      updatePopoverState: PropTypes.func,
      userTier: PropTypes.string,
    }

    constructor(props) {
      super(props)
      this.state = {
        apptTypesToUpdate: [],
        currentApptTypeIds: [],
        dupedOffsets: [],
        searchTerm: '',
        isAddDialogOpen: false,
        isUndoDeleteDialogOpen: false,
        isUpdateDialogOpen: false,
        // store anything you'd get from `#addHandle` in regular React state
        // until user chooses to save in API. Then transfer to Redux
        newHandleSettings: [],
        nextTmpId: 0, // id counter for any handles to store in local state
        reminderSettingErrors: [],
        settingIdsForSlider: [],
        tempSliderName: '',
      }
    }

    componentWillReceiveProps({
      byId,
      selectedApptTypes,
      sliderName,
      sliderSettings,
      settingIds,
    }) {
      const settingIdsForSlider =
        sliderSettings && typeof sliderSettings === 'object'
          ? sliderSettings.map(setting => setting.id)
          : settingIds

      let tempDupedOffsets = []
      settingIdsForSlider.reduce((settingOffsets, settingId) => {
        const foundDupedSetting = settingOffsets.find(
          setting =>
            setting.offset === byId[settingId].offset &&
            setting.type === byId[settingId].message_type,
        )
        if (
          foundDupedSetting &&
          !tempDupedOffsets.includes(foundDupedSetting.id) &&
          !tempDupedOffsets.includes(settingId)
        ) {
          tempDupedOffsets = [
            ...tempDupedOffsets,
            settingId,
            foundDupedSetting.id,
          ]
        }
        return [
          ...settingOffsets,
          {
            offset: byId[settingId].offset,
            type: byId[settingId].message_type,
            id: byId[settingId].id,
          },
        ]
      }, [])
      this.setState({
        currentApptTypeIds: selectedApptTypes,
        tempSliderName: sliderName,
        settingIdsForSlider,
        dupedOffsets: tempDupedOffsets,
      })
    }

    handleClick = event => {
      const { isPopoverOpen, updatePopoverState } = this.props
      event.preventDefault()
      if (!isPopoverOpen) {
        this.setState(
          {
            isAddDialogOpen: true,
            anchorEl: event.currentTarget,
          },
          () => {
            updatePopoverState(true)
          },
        )
      }
    }

    handleRemoveType = typeId => {
      const { currentApptTypeIds } = this.state
      const filteredTypes = currentApptTypeIds.filter(
        chipTypeId => chipTypeId !== typeId,
      )
      this.setState({ currentApptTypeIds: filteredTypes })
    }

    handleAddType = typeId => {
      const { currentApptTypeIds } = this.state
      if (currentApptTypeIds.includes(typeId)) return false
      return this.setState({
        currentApptTypeIds: [...currentApptTypeIds, typeId],
      })
    }

    handleChange = e => {
      this.setState({ tempSliderName: e.target.value })
    }

    addLocalHandle = event => {
      const { messageSettingType, isPostAppt } = this.props
      const { nextTmpId } = this.state

      // Do not add to Redux state. Keep in regular React until user decides
      // create the setting in backend.
      const settingForHandle = {
        active: true,
        always_send: !!isPostAppt,
        message_setting_type: messageSettingType,
        message_type: event.target.innerText.toLowerCase(),
        offset: 0, // TODO: set to number line's "blank, default" space
        body: 'Type message here',
        subject: 'Type subject here',
        custom_setting_id: null,
        tmpId: nextTmpId,
        practice: { pms_type: 'none' },
      }

      const nextHandles = update(this.state.newHandleSettings, {
        $push: [settingForHandle],
      })

      this.setState({
        isAddDialogOpen: false,
        newHandleSettings: nextHandles,
        nextTmpId: nextTmpId + 1,
      })
    }

    // only used for handles that exist in React component state (ie: not yet
    // saved to the backend and Redux)
    removeLocalHandle = tmpId => {
      const { newHandleSettings: currHandleSettings } = this.state

      const nextHandles = update(currHandleSettings, {
        $apply: curr => curr.filter(setting => setting.tmpId !== tmpId),
      })

      this.setState({
        newHandleSettings: nextHandles,
      })
    }

    handleSnackbarOpen = () => {
      this.setState({ isUndoDeleteDialogOpen: true })
    }

    handleUndoAction = () => {
      const { lastRemovedId, undoDeactivate } = this.props
      this.closeAddDialog()
      if (lastRemovedId) {
        return undoDeactivate(lastRemovedId).then(() => this.closeUndoDialog())
      }
      return null
    }

    closeAddDialog = () => {
      const { updatePopoverState } = this.props
      this.setState(
        {
          isAddDialogOpen: false,
        },
        () => {
          updatePopoverState(false)
        },
      )
    }

    toggleApptTypeDialog = () => {
      const { selectedApptTypes, sliderName } = this.props
      const { isUpdateDialogOpen } = this.state
      this.setState({
        isUpdateDialogOpen: !isUpdateDialogOpen,
        currentApptTypeIds: [...selectedApptTypes],
        sliderName,
      })
    }

    handleCustomSettingUpdate = settings =>
      this.props
        .updateCustomMessageSetting(settings)
        .then(this.toggleApptTypeDialog())

    handleCustomSettingDelete = settings =>
      this.props
        .destroyCustomMessageSetting(settings)
        .then(this.toggleApptTypeDialog())

    closeUndoDialog = () => {
      this.setState({ isUndoDeleteDialogOpen: false })
    }

    handleSearch = e => this.setState({ searchTerm: e.target.value })

    render() {
      const {
        appointmentTypesById,
        postAppointmentTypesById,
        availableAppointmentTypesById,
        availablePostAppointmentTypesById,
        availableAppointmentTypeIds,
        availablePostAppointmentTypeIds,
        children,
        id,
        isNegative,
        isPopoverOpen,
        isPostAppt,
        isWeeks,
        locationId,
        name,
        selectedApptTypes,
        sliderName,
        sliderSettings,
        updatePopoverState,
        userTier,
      } = this.props

      const {
        anchorEl,
        currentApptTypeIds,
        dupedOffsets,
        isAddDialogOpen,
        isUpdateDialogOpen,
        newHandleSettings,
        searchTerm,
        settingIdsForSlider,
        tempSliderName,
      } = this.state

      const handles = settingIdsForSlider.reduce((handleProps, settingId) => {
        const hasErrors = !!dupedOffsets.find(dupedId => dupedId === settingId)
        return [
          ...handleProps,
          {
            handleSnackbarOpen: this.handleSnackbarOpen,
            handleUndoAction: this.handleUndoAction,
            hasErrors: !!hasErrors,
            isNegative: !!isNegative,
            isWeeks: !!isWeeks,
            isPopoverOpen,
            isPostAppt,
            key: settingId,
            sliderId: id,
            removeLocalHandle: this.removeLocalHandle,
            settingId,
            sliderName,
            updatePopoverState,
          },
        ]
      }, [])

      const NumberLine = isWeeks
        ? SettingsNumberLineWeeksScale
        : isNegative
          ? SettingsNumberLineNegativeScale
          : SettingsNumberLine

      const numberLineProps = {
        className: 'slider-numberLine',
        style: styles.numberLine,
      }

      const stackedHandlesErrorMsg =
        'There are multiple reminders set for the same day/time on the slider below'

      const isTops = process.env.REACT_APP_IS_TOPS

      return (
        <div style={styles.outerContainer} className="slider-outerContainer">
          {!!dupedOffsets.length && <div>{stackedHandlesErrorMsg}</div>}
          <div style={styles.innerContainer} className="slider-innerContainer">
            {!!sliderName && <div style={styles.sliderLabel}>{sliderName}</div>}
            {sliderSettings && (
              <SliderSettingPill
                styles={isNegative ? styles : altStyles}
                toggleApptTypeDialog={this.toggleApptTypeDialog}
              />
            )}
            <div
              style={
                !sliderName || (sliderName && isNegative) // only customs have sliderName
                  ? styles.pill
                  : altStyles.postPill
              }
              className="slider-pill"
            >
              <span
                style={
                  !sliderName || (sliderName && isNegative)
                    ? styles.span
                    : altStyles.span
                }
              >
                {name}
              </span>
              <PlusOneButton
                handleClick={this.handleClick}
                styles={
                  !sliderName || (sliderName && isNegative)
                    ? styles.plusOneButton
                    : altStyles.plusOneButton
                }
              />
              <AddDialog
                isOpen={isAddDialogOpen}
                anchorEl={anchorEl}
                closeAddDialog={this.closeAddDialog}
                addHandle={this.addLocalHandle}
                userTier={userTier}
              />
            </div>
            <NumberLine {...numberLineProps} />
            <div
              className="slider-handleContainer"
              style={
                !sliderName || (sliderName && isNegative)
                  ? styles.handleContainer
                  : altStyles.handleContainer
              }
            >
              {handles.map(handleProps => (
                <Handle {...handleProps} />
              ))}
              {newHandleSettings.map(setting => (
                <Handle
                  attrForNewSetting={setting}
                  handleSnackbarOpen={this.handleSnackbarOpen}
                  handleUndoAction={this.handleUndoAction}
                  sliderId={id}
                  isNegative={!!isNegative}
                  isWeeks={!!isWeeks}
                  isOpen
                  isPostAppt={isPostAppt}
                  isPopoverOpen={isPopoverOpen}
                  key={`!tmp::${setting.tmpId}`}
                  removeLocalHandle={this.removeLocalHandle}
                  sliderName={sliderName || null}
                  updatePopoverState={updatePopoverState}
                />
              ))}
              {children}
            </div>
            {sliderName && (
              <MessageSettingApptTypePicker
                actions={customApptTypeSliderActions(
                  availableAppointmentTypeIds || [],
                  currentApptTypeIds,
                  this.handleCustomSettingDelete,
                  id,
                  isTops,
                  locationId,
                  tempSliderName,
                  this.toggleApptTypeDialog,
                  this.handleCustomSettingUpdate,
                )}
                sliderAppointmentTypes={
                  isPostAppt
                    ? {
                        ...availablePostAppointmentTypesById,
                        ...selectedApptTypesById(
                          selectedApptTypes,
                          postAppointmentTypesById,
                        ),
                      }
                    : {
                        ...availableAppointmentTypesById,
                        ...selectedApptTypesById(
                          selectedApptTypes,
                          appointmentTypesById,
                        ),
                      }
                }
                sliderAppointmentTypeIds={
                  isPostAppt
                    ? [...availablePostAppointmentTypeIds, ...selectedApptTypes]
                    : [...availableAppointmentTypeIds, ...selectedApptTypes]
                }
                handleAddType={this.handleAddType}
                handleChange={this.handleChange}
                handleRemoveType={this.handleRemoveType}
                handleSearch={this.handleSearch}
                listType="Appointment Types"
                open={isUpdateDialogOpen}
                searchTerm={searchTerm}
                selectedTypes={currentApptTypeIds}
                showSliderNameInput
                sliderId={id}
                sliderName={tempSliderName}
              />
            )}
            <UndoDeleteDialog
              closeUndoDialog={this.closeUndoDialog}
              handleUndoAction={this.handleUndoAction}
              isOpen={this.state.isUndoDeleteDialogOpen}
            />
          </div>
        </div>
      )
    }
  }

  SpecializedSlider.displayName = displayName

  const mapStateToProps = (
    {
      messageSettings: { idsByType, byId, lastRemovedId },
      account: {
        auth: { tier },
        user: { activeLocation },
      },
    },
    ownProps,
  ) => {
    const { messageSettingType, isPostAppt } = ownProps; // Destructure to access isPostAppt
    // Filter settings IDs by the active status, specific messageSettingType, and offset conditions
    const settingIds = idsByType[messageSettingType].reduce(
      (allActiveSettings, settingId) => {
        const setting = byId[settingId];
        // Check if setting matches the messageSettingType and is active
        const matchesTypeAndActive = setting.active && setting.message_setting_type === messageSettingType;
        // Determine if the offset condition is met based on postAppt
        // For postAppt=false, include only settings with a negative offset
        // For postAppt=true, include only settings with a positive offset
        const offsetConditionMet = isPostAppt ? setting.offset > 0 : setting.offset < 0;

        if (matchesTypeAndActive && offsetConditionMet && !allActiveSettings.includes(settingId)) {
          return [...allActiveSettings, settingId];
        }
        return [...allActiveSettings]
      },
      [],
    )
    const locationId = activeLocation ? activeLocation.id : null
    return { byId, lastRemovedId, locationId, settingIds, tier }
  }

  const mapDispatchToProps = dispatch =>
    bindActionCreators(
      {
        ...SettingsActions,
        ...ATActions,
      },
      dispatch,
    )

  return connect(mapStateToProps, mapDispatchToProps)(SpecializedSlider)
}

export default createSlider



// WEBPACK FOOTER //
// ./src/components/Settings/CreateSlider/index.js